import { PropertyHelper } from '@/helpers/property.helper';
import { UserHelper } from '@/helpers/user.helper';
import { Utils } from '@/helpers/utils';
import { AlertService } from '@/services/alert.service';
import { Component, EventEmitter, Inject, Input, LOCALE_ID, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Document, DocumentIdentifier, PropertyCategory, RealProperty, VaultService } from '@eemo/api-client';
import { Subscription } from 'rxjs';
import { FileSizePipe } from '../../extensions/fileSizePipe';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { DocUpdateDialogComponent } from '../doc-update-dialog/doc-update-dialog.component';

import { TooltipDirective } from '@/directives/tooltip/tooltip.directive';
import { ActionService } from '@/services/action.service';
import { NgIf, NgTemplateOutlet } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { ReOrderDialogComponent } from '../re-order-dialog/re-order-dialog.component';

@Component({
  selector: 'app-vault-doc-table',
  templateUrl: './vault-doc-table.component.html',
  styleUrls: ['./vault-doc-table.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    NgTemplateOutlet,
    MatTableModule,
    MatSortModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    FileSizePipe,
    TooltipDirective
  ]
})
export class VaultDocTableComponent implements OnInit, OnDestroy {
  @Input() prop: RealProperty;
  @Input() category: PropertyCategory;
  @ViewChild(MatSort) sort: MatSort;
  @Output() docUpdated = new EventEmitter<boolean>();

  dataSource: MatTableDataSource<Document>;
  displayedColumns = ['icon', 'name', 'type', 'size', 'date', 'description', 'actions'];

  canUpdateProperty = false;

  data: { [key: string]: Document } = {};
  visibleCategories: { [key: string]: boolean } = {};

  sub = new Subscription();
  sortSub = new Subscription();
  deleteSub = new Subscription();
  tokenSub = new Subscription();
  changeOrderSub = new Subscription();

  constructor(
    private vaultService: VaultService,
    public propHelper: PropertyHelper,
    public userHelper: UserHelper,
    private dialog: MatDialog,
    private alertService: AlertService,
    private actionService: ActionService,
    @Inject(LOCALE_ID) public locale: string
  ) {}

  ngOnInit() {
    this.canUpdateProperty = this.userHelper.canUpdateProperty(this.prop);
    this.loadData();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.sortSub.unsubscribe();
    this.deleteSub.unsubscribe();
    this.tokenSub.unsubscribe();
    this.changeOrderSub.unsubscribe();
  }

  loadData() {
    let filter: string;
    if (this.dataSource && this.dataSource.filter) {
      filter = this.dataSource.filter;
    }
    this.sub = this.vaultService.getRealPropertyDocuments(this.prop.uid, this.category).subscribe((data) => {
      // building datasource with displayed data (not source data)
      const displayedData = [];
      data.forEach((i) => {
        const row = {
          doc: i,
          uid: i.uid,
          icon: this.propHelper.getFileIconFromMimeType(i.mimeType),
          iconTooltip: this.propHelper.getFileTooltipFromMimeType(i.mimeType),
          name: i.name,
          docIdentifierName:
            i.identifier !== DocumentIdentifier.NONE ? this.propHelper.getDocumentIdentifierName(i.identifier) : null,
          filename: this.propHelper.getDocumentFilename(i),
          date: Utils.formatDateMonth(i.dateCreate, this.locale),
          category: this.propHelper.getDocumentCategoryName(i.category, this.prop.type),
          type: this.propHelper.getDocumentTypeName(i.type),
          description: i.description,
          size: i.size,
          auto: i.auto,
          certified: i.certified,
          url: this.propHelper.getDocumentUrl(i),
          canUpdate: this.userHelper.canUpdateDocument(this.prop, i),
          canDelete: this.userHelper.canDeleteDocument(this.prop, i),
          viewable: this.propHelper.isDocumentViewable(i)
        };
        displayedData.push(row);
        this.data[i.uid] = i;
      });

      this.dataSource = new MatTableDataSource(displayedData);
      // *ngIf break the sort : must be set in background
      setTimeout(() => (this.dataSource.sort = this.sort));
      if (filter) {
        this.applyFilter(filter);
      }
    });
  }

  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  edit(docToEdit: Document) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(DocUpdateDialogComponent, {
        // width: '20rem'
        data: { doc: docToEdit, prop: this.prop }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.docUpdated.emit(true);
        }
      });
    }
  }

  confirmDelete(doc: Document) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message: 'Confirmez-vous la suppression du document "' + doc.name + '" ?'
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDelete(doc);
        }
      });
    }
  }

  doDelete(doc: Document) {
    this.deleteSub = this.vaultService.deleteDocument(doc.uid).subscribe(() => {
      this.alertService.success('Suppression effectuée avec succès');
      this.loadData();
      this.docUpdated.emit(true);
    });
  }

  changeOrder(doc: Document) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ReOrderDialogComponent, {
        // width: '25rem',
        data: {
          currentOrder: doc.order,
          titleSuffix: 'document'
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doChangeOrder(doc, result);
        }
      });
    }
  }

  doChangeOrder(doc: Document, newOrder: number) {
    this.changeOrderSub = this.vaultService.updateDocumentOrder(doc.uid, newOrder).subscribe(() => {
      this.alertService.success('Modification effectuée avec succès');
      this.loadData();
      this.docUpdated.emit(true);
    });
  }

  viewDoc(doc: Document) {
    this.actionService.viewDoc(doc);
  }
}
